/**
 * BprSectionDiagram - embeds ONE state of a questionnaire section's
 * process-flow pair inside the deal Questions panel (BPR-category deals).
 *
 * Per the BPR workflow, every captured section OPENS with its Current State
 * business-process flow (rendered right after the section heading, before the
 * first question) and CLOSES with the Future State flow (after the last
 * question, before the next section) - so each embed renders a single fixed
 * state instead of a tab pair. The future state re-derives from the section's
 * saved questionnaire answers (the shared dealDiagramsStore refreshes it
 * after Save, and this embed shows the in-flight state). The diagrams are the
 * same cached specs the engagement stores (one bpr_output batch, keyed by
 * questionnaire sectionKey); one fetch per deal is shared by every section
 * embed via the store. Sections without a diagram render nothing; non-BPR
 * deals have no linked engagement, so the fetch resolves empty and nothing
 * shows.
 */

import type { BprDiagramNode, BprDiagramState } from '@protoss/shared';
import { Pill } from '@protoss/ui';
import * as React from 'react';
import { sectionWorkflows, useDealDiagrams } from '../services/dealDiagramsStore';
import { BprDiagramCanvas } from './BprDiagramCanvas';
import { BprDiagramLegend } from './BprDiagramLegend';

export interface BprSectionDiagramProps {
  dealUuid: string;
  sectionKey: string;
  /** Which state this embed draws: 'current' opens the section, 'future' closes it. */
  state: BprDiagramState;
}

export function BprSectionDiagram({ dealUuid, sectionKey, state }: BprSectionDiagramProps) {
  const { diagrams, refreshingSections, generatingSections } = useDealDiagrams(dealUuid);
  const [selectedNode, setSelectedNode] = React.useState<BprDiagramNode | null>(null);

  const { current, future } = React.useMemo(
    () => sectionWorkflows(diagrams, sectionKey),
    [diagrams, sectionKey]
  );
  // A pair top-up (section had no flow yet) redraws both embeds; a post-Save
  // future-state refresh only touches the future embed.
  const isGeneratingPair = generatingSections.has(sectionKey);
  const isPending =
    state === 'future' ? isGeneratingPair || refreshingSections.has(sectionKey) : isGeneratingPair;
  const active = state === 'future' ? future : current;

  if (!active && !isPending) return null;

  return (
    <div
      className={`bpr-section-diagram${state === 'current' ? ' bpr-section-diagram--opening' : ''}`}
    >
      <div className="bpr-section-diagram__head">
        <span className="bpr-section-diagram__title">
          {state === 'future' ? 'Future state process flow' : 'Current state process flow'}
        </span>
        {isPending ? (
          <Pill variant="warning">
            {isGeneratingPair ? 'Generating process flow…' : 'Updating future state…'}
          </Pill>
        ) : (
          <span className="bpr-section-diagram__hint">click a node for its findings</span>
        )}
      </div>
      {active ? (
        <>
          <BprDiagramCanvas
            key={active.uuid}
            spec={active.spec}
            onSelectNode={setSelectedNode}
            exportTitle={active.title}
          />
          <BprDiagramLegend />
        </>
      ) : (
        <p className="bpr-section-diagram__pending">
          {isGeneratingPair
            ? 'Drawing this section’s process flow…'
            : 'Regenerating this section’s future state from your saved answers…'}
        </p>
      )}
      {selectedNode ? (
        <div className="bpr-section-diagram__issues">
          <span className="bpr-section-diagram__issues-title">{selectedNode.label}</span>
          <Pill variant="ghost">{selectedNode.system}</Pill>
          {selectedNode.issues.length === 0 ? (
            <span className="bpr-section-diagram__issues-empty">no linked issues</span>
          ) : (
            <ul className="bpr-section-diagram__issues-list">
              {selectedNode.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
